import { DiagramComponent } from './DiagramComponent';

/**
 * Event for diagram interactions
 */
export class DiagramEvent {
  type: string;
  detail: Record<string, any>;
  source: DiagramComponent;
  consumed: boolean;
  timestamp: number;

  constructor(type: string, detail: Record<string, any>, source: DiagramComponent) {
    this.type = type;
    this.detail = detail;
    this.source = source;
    this.consumed = false;
    this.timestamp = Date.now();
  }

  /**
   * Stop event propagation
   */
  consume(): void {
    this.consumed = true;
  }

  /**
   * Check if event was consumed
   */
  isConsumed(): boolean {
    return this.consumed;
  }
}

/**
 * Event system for component interaction
 * Implements DOM-style event bubbling with consumption
 */
export class EventSystem {
  private listeners: Map<string, Set<(event: DiagramEvent) => void>>;

  constructor() {
    this.listeners = new Map();
  }

  /**
   * Add event listener
   */
  on(type: string, handler: (event: DiagramEvent) => void): void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)!.add(handler);
  }

  /**
   * Remove event listener
   */
  off(type: string, handler: (event: DiagramEvent) => void): void {
    const handlers = this.listeners.get(type);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Emit event with bubbling
   */
  emit(event: DiagramEvent, bubblePath: DiagramComponent[]): void {
    for (const component of bubblePath) {
      // Offer event to component
      const consumed = component.handleEvent(event);

      if (consumed || event.isConsumed()) {
        break; // Stop bubbling
      }
    }

    // Fire global listeners (even if consumed)
    const handlers = this.listeners.get(event.type);
    if (handlers) {
      handlers.forEach(handler => handler(event));
    }
  }

  /**
   * Create event from user interaction
   */
  createEvent(type: string, detail: Record<string, any>, source: DiagramComponent): DiagramEvent {
    return new DiagramEvent(type, detail, source);
  }

  /**
   * Clear all listeners
   */
  clear(): void {
    this.listeners.clear();
  }
}
